import { useState, useEffect } from 'react';
import { AppBar, Box, Typography, IconButton, iconButtonClasses, styled, Tab } from '@mui/material';
import Tabs, { tabsClasses } from '@mui/material/Tabs';
import { ServiceCategory } from 'orderme-api-integration-client';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { apiKey, imagesUri } from '../../helpers/clientConfigs';
import { matchPath, useLocation } from 'react-router-dom';
import { useRouteNavigator } from '../../routes/useRouteNavigator';
import { CategoryGroupRoute, CategoryRoute, GoodsItemRoute, GoodsRoute } from '../../routes/allRoutes';
import { RootState, store } from '../../redux/store';
import { isGoodsCategory } from '../../helpers/servicesHelper';
import { useAppSelector } from '../../redux/hooks';

const imageSize = 60;

const ScrollIconButton = styled(IconButton)({
  width: '40px',
  color: 'rgba(255, 255, 255, 1)',
  [`&.${iconButtonClasses.disabled}`]: { color: 'rgba(255, 255, 255, 0.3)' },
});

const StartScrollButton = () => {
  return (
    <ScrollIconButton disableRipple sx={{ marginRight: '4px' }}>
      <ArrowBackIosIcon sx={{ width: '40px', height: '40px' }} />
    </ScrollIconButton>
  );
};

const EndScrollButton = () => {
  return (
    <ScrollIconButton disableRipple sx={{ marginLeft: '2px' }}>
      <ArrowForwardIosIcon sx={{ width: '40px', height: '40px' }} />
    </ScrollIconButton>
  );
};

export function CategoriesAppBar() {
  const location = useLocation();
  const routeNavigator = useRouteNavigator();
  const serviceCategories: ServiceCategory[] = useAppSelector((state: RootState) => state.categoriesState.categories);
  const [goodsCategory, setGoodsCategory] = useState<ServiceCategory>();

  const shouldCenter = serviceCategories.length <= 4;

  useEffect(() => {
    setGoodsCategory(serviceCategories?.find(isGoodsCategory));
  }, [serviceCategories]);

  const getWebsiteCategoryGrid = (category: ServiceCategory) => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <img
          width={imageSize}
          height={imageSize}
          src={`${imagesUri}/${category.logoId}?orderme-api-key=${apiKey}&width=${imageSize * 2}`}
          alt={category.name}
        />
        <Typography
          sx={{
            color: 'white',
            pt: '5px',
            fontSize: { xs: '12px', sm: '14px', md: '18px' },
            textAlign: 'center',
            width: { xs: '80px', sm: '100px', md: 'auto' },
          }}
        >
          {category.name}
        </Typography>
      </Box>
    );
  };

  function getCurrentTab() {
    if (!serviceCategories) {
      return null;
    }

    const pathname = location.pathname;
    var match = matchPath(CategoryRoute.getPath(), pathname) || matchPath(CategoryGroupRoute.getPath(), pathname);
    const categoryId = match?.params['categoryId'];

    if (categoryId) {
      var categoryLink = store.getState().linksState.categories.find((link) => link.id === categoryId);
      if (categoryLink) {
        return categoryLink?.dataId;
      }
    } else if (goodsCategory) {
      // Check if physical items category
      match = matchPath(GoodsRoute.getPath(), pathname) || matchPath(GoodsItemRoute.getPath(), pathname);
      if (match) {
        return goodsCategory?.id;
      }
    }

    return null;
  }

  const currentTab = getCurrentTab();

  return (
    <Box
      sx={{
        width: '100%',
        mb: '20px',
      }}
    >
      <AppBar
        sx={{ backgroundColor: 'appBar.top.background', color: 'appBar.top.color' }}
        elevation={0}
        position="static"
        color="default"
      >
        <Tabs
          sx={{
            width: '100%',
            [`& .${tabsClasses.scrollButtons}`]: {
              '&.Mui-disabled': { opacity: 0.3 },
            },
          }}
          allowScrollButtonsMobile
          value={currentTab}
          variant="scrollable"
          slotProps={{
            indicator: {
              sx: {
                backgroundColor: 'secondary.main',
                color: 'secondary.contrastText',
                height: '5px',
              },
            },
          }}
          slots={{
            StartScrollButtonIcon: StartScrollButton,
            EndScrollButtonIcon: EndScrollButton,
          }}
        >
          {serviceCategories?.map((category, index) => (
            <Tab
              sx={{
                padding: '6px 6px',
                minWidth: 'fit-content',
                flex: 1,
                justifyContent: 'flex-start',
                ...(shouldCenter && index === 0 ? { ml: 'auto' } : {}),
                ...(shouldCenter && index === serviceCategories.length - 1 ? { mr: 'auto' } : {}),
              }}
              value={category.id}
              key={category.id}
              onClick={() => {
                if (currentTab === category.id && category !== goodsCategory) {
                  return;
                }
                routeNavigator.goToCategory(category);
              }}
              label={getWebsiteCategoryGrid(category)}
            />
          ))}
        </Tabs>
      </AppBar>
    </Box>
  );
}
